import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { ExportMenu } from "@/components/ExportMenu";
import { PreviewButton } from "@/components/PreviewButton";
import { LineItemsEditor, computeLineTotal } from "@/components/LineItemsEditor";
import { Plus, CheckCircle, Star, Scales } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ArchiveButton } from "@/components/ArchiveButton";

const EMPTY = { budget_request_id: "", supplier_id: "", supplier_name: "", client_id: "", client_name: "", subject: "", valid_until: "", items: [], terms: "", attachments: [], direction: "inbound" };

export default function QuotationsPage() {
  const { user } = useAuth();
  const [quotations, setQuotations] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [compareBudgetId, setCompareBudgetId] = useState("");
  const [direction, setDirection] = useState("inbound");

  async function load() {
    const [q, b, s, c] = await Promise.all([
      api.get(`/quotations?direction=${direction}`),
      api.get("/budget-requests"),
      api.get("/suppliers"),
      api.get("/clients"),
    ]);
    setQuotations(q.data);
    setBudgets(b.data);
    setSuppliers(s.data);
    setClients(c.data);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [direction]);

  const canManage = can(user, ["procurement"]);
  const canReview = can(user, ["procurement", "finance"]);

  function openCreate() { setEditing(null); setForm({ ...EMPTY, direction }); setOpen(true); }
  function openEdit(q) {
    setEditing(q);
    setForm({
      budget_request_id: q.budget_request_id || "",
      supplier_id: q.supplier_id || "",
      supplier_name: q.supplier_name,
      client_id: q.client_id || "",
      client_name: q.client_name || "",
      subject: q.subject,
      valid_until: q.valid_until || "",
      items: q.items || [],
      terms: q.terms || "",
      attachments: q.attachments || [],
      direction: q.direction || "inbound",
    });
    setOpen(true);
  }

  async function submit(e) {
    e.preventDefault();
    const payload = { ...form, total_amount: computeLineTotal(form.items) };
    try {
      if (editing) {
        await api.put(`/quotations/${editing.id}`, payload);
        toast.success("Quotation updated");
      } else {
        await api.post("/quotations", payload);
        toast.success("Quotation added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  }

  async function selectQuote(qid) {
    try {
      await api.post(`/quotations/${qid}/select`);
      toast.success("Selected");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }
  async function approve(qid) {
    try {
      await api.post(`/quotations/${qid}/review`, { status: "reviewed", note: "Reviewed" });
      toast.success("Marked reviewed");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }

  // ---- Complete client (outbound) quotation with approved-file upload ----
  const [completing, setCompleting] = useState(null);          // quotation being completed
  const [approvedFile, setApprovedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  async function submitComplete(e) {
    e.preventDefault();
    if (!completing) return;
    if (!approvedFile && !(completing.attachments || []).length) {
      toast.error("Please attach the approved quotation file first.");
      return;
    }
    setUploading(true);
    try {
      if (approvedFile) {
        const fd = new FormData();
        fd.append("file", approvedFile);
        const { data: up } = await api.post("/uploads/quotations", fd, { headers: { "Content-Type": "multipart/form-data" } });
        await api.post(`/quotations/${completing.id}/attachments`, { attachments: [up] });
      }
      await api.post(`/quotations/${completing.id}/review`, { status: "completed", note: "Approved file uploaded" });
      toast.success("Quotation completed");
      setCompleting(null); setApprovedFile(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to complete");
    } finally { setUploading(false); }
  }

  const budgetOptions = budgets.filter((b) => b.status === "in_progress" || b.status === "completed" || b.status === "open");
  // Quotation status label mapping is now handled directly by StatusBadge (reviewed → "Reviewed", completed → "Completed").
  const qtLabel = () => undefined;

  // For comparison view
  const comparable = compareBudgetId ? quotations.filter((q) => q.budget_request_id === compareBudgetId) : [];

  return (
    <div className="space-y-6" data-testid="quotations-page">
      <div className="flex items-end justify-between border-b border-border pb-6">
        <div>
          <div className="section-caption mb-2">Module 03</div>
          <h1 className="section-title text-3xl sm:text-4xl">Quotations</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {direction === "inbound"
              ? "Collect, compare and approve quotations received from suppliers."
              : "Quotations issued by Casedist Inc. to clients (outbound)."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex border border-border rounded-md overflow-hidden">
            <button
              onClick={() => setDirection("inbound")}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest ${direction === "inbound" ? "bg-foreground text-background" : "hover:bg-muted"}`}
              data-testid="direction-inbound-btn"
            >From suppliers</button>
            <button
              onClick={() => setDirection("outbound")}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest border-l border-border ${direction === "outbound" ? "bg-foreground text-background" : "hover:bg-muted"}`}
              data-testid="direction-outbound-btn"
            >To clients</button>
          </div>
          {canManage && (
            <Button onClick={openCreate} data-testid="new-quotation-btn">
              <Plus size={16} className="mr-2" /> {direction === "outbound" ? "New Client Quote" : "New Quotation"}
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="list">
        <TabsList className="border border-border">
          <TabsTrigger value="list" data-testid="tab-list">All quotations</TabsTrigger>
          <TabsTrigger value="compare" data-testid="tab-compare">Compare</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-4">
          <div className="border border-border rounded-md bg-card overflow-x-auto">
            <Table className="min-w-[1200px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Quote No.</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>{direction === "outbound" ? "Client" : "Supplier"}</TableHead>
                  <TableHead>Budget Req.</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Selected</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quotations.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">No quotations yet.</TableCell></TableRow>
                ) : quotations.map((q) => {
                  const bud = budgets.find((b) => b.id === q.budget_request_id);
                  return (
                    <TableRow key={q.id} data-testid={`quotation-row-${q.id}`}>
                      <TableCell className="font-mono text-xs">{q.quotation_no}</TableCell>
                      <TableCell className="font-medium">{q.subject}</TableCell>
                      <TableCell>{direction === "outbound" ? (q.client_name || "—") : q.supplier_name}</TableCell>
                      <TableCell className="font-mono text-xs">{bud?.request_no || "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">₱{(q.total_amount || 0).toLocaleString()}</TableCell>
                      <TableCell>{q.is_selected ? <Star size={16} weight="fill" className="text-amber-500" /> : <span className="text-muted-foreground text-xs">—</span>}</TableCell>
                      <TableCell><StatusBadge status={q.status} labelOverride={qtLabel(q.status)} /></TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {canManage && !q.is_selected && q.status !== "rejected" && (
                            <Button size="sm" variant="outline" onClick={() => selectQuote(q.id)} className="border-border" data-testid={`select-quotation-${q.id}`}>Select</Button>
                          )}
                          {canReview && q.status === "open" && (
                            <Button size="sm" variant="outline" onClick={() => approve(q.id)} className="border-border" data-testid={`approve-quotation-${q.id}`}>
                              <CheckCircle size={14} className="mr-1" /> Review
                            </Button>
                          )}
                          {canReview && q.status === "reviewed" && direction === "outbound" && (
                            <Button size="sm" onClick={() => { setCompleting(q); setApprovedFile(null); }} data-testid={`complete-quotation-${q.id}`}>
                              <CheckCircle size={14} className="mr-1" /> Complete
                            </Button>
                          )}
                          {canManage && <Button size="sm" variant="outline" className="border-border" onClick={() => openEdit(q)} data-testid={`edit-quotation-${q.id}`}>Edit</Button>}
                          <PreviewButton endpointPath={`/exports/quotation/${q.id}`} baseName={`quotation_${q.quotation_no}`} title={`Quotation — ${q.quotation_no}`} testId={`preview-quotation-${q.id}`} />
                          <ExportMenu endpointPath={`/exports/quotation/${q.id}`} baseName={`quotation_${q.quotation_no}`} testId={`export-quotation-${q.id}`} />
                          {(user?.role === "admin" || q.submitted_by === user?.id) && !q.deleted_at && (
                            <ArchiveButton endpoint={`/quotations/${q.id}`} label={q.quotation_no} onArchived={load} testId={`archive-quotation-${q.id}`} />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="compare" className="mt-4 space-y-4">
          <div className="flex items-center gap-3 border-b border-border pb-4">
            <Scales size={18} className="text-muted-foreground" />
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Budget Request</Label>
            <div className="w-96">
              <Select value={compareBudgetId} onValueChange={setCompareBudgetId}>
                <SelectTrigger data-testid="compare-budget-select"><SelectValue placeholder="Pick a budget request to compare quotes" /></SelectTrigger>
                <SelectContent>
                  {budgetOptions.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.request_no} — {b.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {!compareBudgetId ? (
            <div className="text-sm text-muted-foreground border border-dashed border-border rounded-md p-8 text-center">
              Select a budget request to compare its quotations side-by-side.
            </div>
          ) : comparable.length === 0 ? (
            <div className="text-sm text-muted-foreground border border-dashed border-border rounded-md p-8 text-center">
              No quotations linked to this budget request yet.
            </div>
          ) : (
            <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${Math.min(comparable.length, 3)}, minmax(0, 1fr))` }}>
              {comparable.map((q) => (
                <div key={q.id} className={`border rounded-md p-5 ${q.is_selected ? "border-foreground bg-foreground text-background" : "border-border bg-card"}`} data-testid={`compare-card-${q.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className={`text-[10px] uppercase tracking-widest ${q.is_selected ? "text-background/60" : "text-muted-foreground"}`}>{q.quotation_no}</div>
                    {q.is_selected && <Star size={14} weight="fill" className="text-amber-400" />}
                  </div>
                  <div className="font-heading font-bold text-lg">{q.supplier_name}</div>
                  <div className={`text-xs mb-4 ${q.is_selected ? "text-background/70" : "text-muted-foreground"}`}>{q.subject}</div>
                  <div className="mb-4">
                    <div className={`text-[10px] uppercase tracking-widest ${q.is_selected ? "text-background/60" : "text-muted-foreground"}`}>Total</div>
                    <div className="font-heading font-black text-2xl tabular-nums">₱{(q.total_amount || 0).toLocaleString()}</div>
                  </div>
                  <div className="space-y-1 border-t border-dashed py-3 text-xs" style={{ borderColor: q.is_selected ? "rgba(255,255,255,0.15)" : "hsl(var(--border))" }}>
                    <div className="flex justify-between"><span className={q.is_selected ? "text-background/60" : "text-muted-foreground"}>Line items</span><span className="tabular-nums">{q.items?.length || 0}</span></div>
                    <div className="flex justify-between"><span className={q.is_selected ? "text-background/60" : "text-muted-foreground"}>Valid until</span><span>{q.valid_until || "—"}</span></div>
                    <div className="flex justify-between"><span className={q.is_selected ? "text-background/60" : "text-muted-foreground"}>Status</span><StatusBadge status={q.status} labelOverride={qtLabel(q.status)} /></div>
                  </div>
                  {!q.is_selected && canManage && (
                    <Button size="sm" onClick={() => selectQuote(q.id)} className="w-full mt-3" data-testid={`compare-select-${q.id}`}>Select this quote</Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading font-black">{editing ? `Edit ${editing.quotation_no}` : "New Quotation"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            {form.direction === "outbound" ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Bill To (Client)</Label>
                  <Select value={form.client_id} onValueChange={(v) => { const cl = clients.find((c) => c.id === v); setForm({ ...form, client_id: v, client_name: cl?.name || "", supplier_name: "Casedist Inc." }); }}>
                    <SelectTrigger data-testid="quotation-outbound-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                    <SelectContent>
                      {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="Or enter client name" required className="border-border" data-testid="quotation-outbound-client-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Issued By (Us)</Label>
                  <Input value={form.supplier_name || "Casedist Inc."} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} required className="border-border" data-testid="quotation-outbound-supplier-name" />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Supplier</Label>
                  <Select value={form.supplier_id} onValueChange={(v) => {
                    const sp = suppliers.find((s) => s.id === v);
                    setForm({ ...form, supplier_id: v, supplier_name: sp?.name || form.supplier_name });
                  }}>
                    <SelectTrigger data-testid="quotation-supplier-select"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                    <SelectContent>
                      {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} placeholder="Or enter supplier name" required className="border-border" data-testid="quotation-supplier-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Linked Budget</Label>
                  <Select value={form.budget_request_id} onValueChange={(v) => setForm({ ...form, budget_request_id: v })}>
                    <SelectTrigger data-testid="quotation-budget-select"><SelectValue placeholder="Link to budget request" /></SelectTrigger>
                    <SelectContent>
                      {budgetOptions.map((b) => <SelectItem key={b.id} value={b.id}>{b.request_no} — {b.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Subject</Label>
                <Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="border-border" data-testid="quotation-subject-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Valid Until</Label>
                <Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className="border-border" data-testid="quotation-valid-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">{form.direction === "outbound" ? "Project / Reference (optional)" : "Linked Client (optional)"}</Label>
              {form.direction === "outbound" ? (
                <Input value={form.terms || ""} onChange={() => {}} className="hidden" />
              ) : (
                <Select value={form.client_id} onValueChange={(v) => { const cl = clients.find((c) => c.id === v); setForm({ ...form, client_id: v, client_name: cl?.name || "" }); }}>
                  <SelectTrigger data-testid="quotation-client-select"><SelectValue placeholder="Select client this quote is for" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <LineItemsEditor items={form.items} onChange={(it) => setForm({ ...form, items: it })} />
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Terms</Label>
              <Textarea rows={2} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} className="border-border" data-testid="quotation-terms-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="quotation-submit-btn">{editing ? "Save" : "Add quotation"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Complete client quotation — requires approved-file upload */}
      <Dialog open={!!completing} onOpenChange={(v) => !v && (setCompleting(null), setApprovedFile(null))}>
        <DialogContent data-testid="complete-quotation-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black">Complete — {completing?.quotation_no}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitComplete} className="space-y-3">
            <div className="p-3 border border-border rounded-md bg-muted/30 text-sm">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Client</div>
              <div className="font-medium">{completing?.client_name || "—"}</div>
              <div className="text-muted-foreground">{completing?.subject} · <span className="tabular-nums">₱{(completing?.total_amount || 0).toLocaleString()}</span></div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Approved Quotation File <span className="text-rose-600">*</span></Label>
              <Input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(e) => setApprovedFile(e.target.files?.[0] || null)}
                className="border-border"
                data-testid="complete-quotation-file-input"
              />
              {(completing?.attachments || []).length > 0 && (
                <p className="text-[11px] text-muted-foreground">{(completing.attachments || []).length} file(s) already attached — uploading a new one is optional.</p>
              )}
              <p className="text-[10px] text-muted-foreground">Attach the signed / approved copy from the client. Required to move status to Completed.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => { setCompleting(null); setApprovedFile(null); }}>Cancel</Button>
              <Button type="submit" disabled={uploading} data-testid="complete-quotation-submit-btn">
                <CheckCircle size={16} className="mr-2" />{uploading ? "Completing…" : "Upload & Complete"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
